import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/components/feature/AdminAuthContext';
import Logo from '@/components/feature/Logo';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, isLoading } = useAdminAuth();
  const navigate = useNavigate();

  if (!isLoading && isAuthenticated) {
    navigate('/admin', { replace: true });
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    if (result.success) {
      navigate('/admin', { replace: true });
    } else {
      setError(result.error || 'Invalid email or password. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo variant="dark" className="w-[200px] md:w-[240px]" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900">Admin Portal</h1>
          <p className="text-sm text-foreground-600 mt-1">Premium Visa Solutions - Staff Access</p>
        </div>

        <div className="bg-background-50 border border-secondary-200 rounded-lg p-6 md:p-8">
          <h2 className="font-heading font-semibold text-lg text-foreground-900 mb-6">Sign In</h2>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3 mb-5 flex items-start gap-2">
              <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-error-warning-line text-sm"></i>
              </div>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="admin-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Email Address
              </label>
              <input
                id="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="staff@pvs-zm.com"
                className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              />
            </div>
            <div className="flex items-center justify-between text-xs">
              <label className="flex items-center gap-2 cursor-pointer text-foreground-600">
                <input type="checkbox" className="w-4 h-4 text-primary-500 border-secondary-300 rounded focus:ring-primary-400 cursor-pointer" />
                Remember me
              </label>
              <button type="button" className="text-accent-600 hover:text-accent-700 font-medium cursor-pointer whitespace-nowrap">
                Forgot password?
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-md hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Signing in...
                </span>
              ) : (
                'Secure Login'
              )}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-secondary-200">
            <p className="text-xs text-foreground-500 text-center">
              This portal is for Premium Visa Solutions employees only.
              <br />
              Unauthorized access is prohibited.
            </p>
          </div>
        </div>

        <p className="text-xs text-foreground-400 text-center mt-6">
          &copy; {new Date().getFullYear()} Premium Visa Solutions. All rights reserved.
        </p>
      </div>
    </div>
  );
}